import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  scheduleApplyBodyHeaderCssVars,
} from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Settles sticky state after scrolling stops (avoids attribute flicker at rest).
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #scrollIdleTimer = null;

  /**
   * Last committed sticky-active boolean for hysteresis (sticky="always").
   * @type {boolean | null}
   */
  #stickyActiveCommitted = null;

  /**
   * Scroll distance required before sticky "active" is enabled.
   * @type {number}
   */
  #downDirectionActivateThreshold = 80;

  /**
   * Lower threshold to keep state stable (hysteresis).
   * @type {number}
   */
  #downDirectionDeactivateThreshold = 20;

  /**
   * Ignore tiny delta changes to prevent jitter flicker.
   * @type {number}
   */
  #scrollDirectionDeltaThreshold = 8;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(() => {
    scheduleApplyBodyHeaderCssVars();

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        // Keep sticky state controlled by scroll logic for stability.
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollIdleTimer != null) {
      clearTimeout(this.#scrollIdleTimer);
    }

    this.#scrollIdleTimer = setTimeout(() => {
      this.#scrollIdleTimer = null;
      this.#updateScrollState(true);
    }, 140);

    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState(false);
    });
  };

  /**
   * @param {boolean} [isScrollIdle] - True when scroll has stopped; allows final state commit.
   */
  #updateScrollState = (isScrollIdle = false) => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);
    const headerTop = this.getBoundingClientRect().top;
    const scrollDelta = scrollTop - this.#lastScrollTop;
    const isScrollingUp = scrollDelta < 0;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (this.#stickyActiveCommitted === null) {
        this.#stickyActiveCommitted = this.dataset.stickyState === 'active';
      }

      const shouldBeActive = this.#stickyActiveCommitted
        ? scrollTop > this.#downDirectionDeactivateThreshold
        : scrollTop >= this.#downDirectionActivateThreshold;

      const hasMeaningfulScroll =
        isScrollIdle ||
        Math.abs(scrollDelta) >= this.#scrollDirectionDeltaThreshold ||
        Math.abs(scrollTop - this.#lastScrollTop) >= this.#scrollDirectionDeltaThreshold;

      if (!hasMeaningfulScroll) {
        return;
      }

      const nextStickyState = shouldBeActive ? 'active' : 'inactive';
      if (this.dataset.stickyState !== nextStickyState) {
        this.dataset.stickyState = nextStickyState;
      }
      this.#stickyActiveCommitted = shouldBeActive;

      let nextScrollDirection = 'none';
      if (shouldBeActive && !isAtTop && !isScrollIdle) {
        if (Math.abs(scrollDelta) >= this.#scrollDirectionDeltaThreshold) {
          nextScrollDirection = isScrollingUp ? 'up' : 'down';
        } else {
          nextScrollDirection = this.dataset.scrollDirection || 'none';
        }
      }

      if (this.dataset.scrollDirection !== nextScrollDirection) {
        this.dataset.scrollDirection = nextScrollDirection;
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#lastScrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#scrollIdleTimer != null) {
      clearTimeout(this.#scrollIdleTimer);
      this.#scrollIdleTimer = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      scheduleApplyBodyHeaderCssVars();
    });

    resizeObserver.observe(headerGroup);

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    for (let i = 0; i < headerGroup.children.length; i++) {
      const element = headerGroup.children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (let i = 0; i < headerGroup.children.length; i++) {
            const element = headerGroup.children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
          scheduleApplyBodyHeaderCssVars({ force: true });
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
    scheduleApplyBodyHeaderCssVars({ force: true });
  }
});
